import {
  Alert,
  Box,
  Card,
  Chip,
  Collapse,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { foyerApi, type ClientErrorGroup } from "../http/foyer";
import { useFormat } from "../i18n/format";
import { errorText } from "../utils/apiError";

const APPS = ["tv", "cms", "guest", "family", "relative", "video"] as const;
type App = (typeof APPS)[number];
const isApp = (a: string): a is App => (APPS as readonly string[]).includes(a);

const short = (release: string) =>
  /^[0-9a-f]{12,}$/i.test(release) ? release.slice(0, 7) : release;

/**
 * Uncaught JS errors from every Foyer front-end, grouped by fingerprint.
 * Nenda staff only (canManageSettings on the route and the nav item).
 * Per-screen TV logs live in the device dialog; this page answers "which
 * errors are happening across all customers, and since which release".
 */
export default function ClientErrorsPage() {
  const [days, setDays] = useState(7);
  const [app, setApp] = useState("");
  const [groups, setGroups] = useState<ClientErrorGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const { t } = useTranslation(["analytics", "common"]);
  const fmt = useFormat();

  useEffect(() => {
    let live = true;
    setGroups(null);
    setError(null);
    foyerApi
      .clientErrors(days, app || undefined)
      .then((r) => live && setGroups(r.groups))
      .catch((e) => live && setError(errorText(e, t)));
    return () => {
      live = false;
    };
  }, [days, app, t]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("clientErrorsPage.eyebrow")}
        title={t("clientErrorsPage.title")}
        subtitle={t("clientErrorsPage.subtitle")}
      />

      <Stack direction="row" spacing={2}>
        <TextField
          select
          size="small"
          label={t("clientErrorsPage.app")}
          value={app}
          onChange={(e) => setApp(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{t("clientErrorsPage.apps.all")}</MenuItem>
          {APPS.map((a) => (
            <MenuItem key={a} value={a}>
              {t(`clientErrorsPage.apps.${a}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t("clientErrorsPage.window")}
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          sx={{ minWidth: 140 }}
        >
          {[1, 7, 30].map((d) => (
            <MenuItem key={d} value={d}>
              {d === 1
                ? t("clientErrorsPage.todayUtc")
                : t("common:labels.lastDays", { count: d })}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {error && (
        <Alert severity="error">
          {t("clientErrorsPage.loadFailed", { error })}
        </Alert>
      )}
      {groups && groups.length === 0 && (
        <Alert severity="success">{t("clientErrorsPage.none")}</Alert>
      )}
      {!groups && !error && (
        <Typography color="text.secondary">
          {t("common:states.loading")}
        </Typography>
      )}

      {groups && groups.length > 0 && (
        <Card variant="outlined">
          {groups.map((g, i) => (
            <Fragment key={g.fingerprint}>
              <Box
                role="button"
                tabIndex={0}
                data-testid="client-error-group"
                onClick={() =>
                  setOpen(open === g.fingerprint ? null : g.fingerprint)
                }
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  setOpen(open === g.fingerprint ? null : g.fingerprint)
                }
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  borderTop: i ? "1px solid" : "none",
                  borderColor: "divider",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="baseline">
                  <Typography
                    variant="h6"
                    component="span"
                    sx={{ minWidth: 56, textAlign: "right" }}
                  >
                    {fmt.number(g.count)}
                  </Typography>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "fontFamilyMono",
                        wordBreak: "break-word",
                      }}
                    >
                      {g.message}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}
                    >
                      <Chip
                        size="small"
                        label={
                          isApp(g.app)
                            ? t(`clientErrorsPage.apps.${g.app}`)
                            : g.app
                        }
                      />
                      {g.kind !== "error" && (
                        <Chip size="small" variant="outlined" label={g.kind} />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {t(
                          g.lastRelease !== g.firstRelease
                            ? "clientErrorsPage.seenStillIn"
                            : "clientErrorsPage.seen",
                          {
                            days: t("clientErrorsPage.days", { count: g.days }),
                            lastSeen: fmt.dateTime(g.lastSeenAt),
                            release: short(g.firstRelease),
                            lastRelease: short(g.lastRelease),
                          }
                        )}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
              <Collapse in={open === g.fingerprint} unmountOnExit>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("clientErrorsPage.path", { path: g.lastPath })}
                    {g.lastUserAgent && ` · ${g.lastUserAgent}`}
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      mt: 1,
                      p: 1,
                      typography: "micro",
                      overflow: "auto",
                      maxHeight: 280,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                    }}
                  >
                    {g.stack ?? t("clientErrorsPage.noStack")}
                  </Box>
                </Box>
              </Collapse>
            </Fragment>
          ))}
        </Card>
      )}
    </PageContainer>
  );
}
